import { useState } from "react";
import { LockKeyhole, Share2 } from "lucide-react";

import type {
  OpsDocAccessLevel,
  OpsDocAccessMode,
  OpsDocEmployeePrincipal,
  OpsDocGroup,
  OpsDocPage,
} from "../../shared/operations-docs-schemas";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";

type Entry = {
  subjectType: "employee" | "group";
  subjectId: string;
  accessLevel: OpsDocAccessLevel;
};

function SubjectRow({
  checked,
  label,
  level,
  onCheckedChange,
  onLevelChange,
}: {
  checked: boolean;
  label: string;
  level: OpsDocAccessLevel;
  onCheckedChange: (checked: boolean) => void;
  onLevelChange: (level: OpsDocAccessLevel) => void;
}) {
  return (
    <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 rounded-sm border px-3 py-2">
      <input
        aria-label={`Share with ${label}`}
        checked={checked}
        className="size-4 accent-primary"
        onChange={(event) => {
          onCheckedChange(event.target.checked);
        }}
        type="checkbox"
      />
      <span className="min-w-0 truncate text-sm font-medium">{label}</span>
      <select
        aria-label={`Access for ${label}`}
        className="min-h-9 rounded-sm border bg-card px-2 text-sm"
        disabled={!checked}
        onChange={(event) => {
          onLevelChange(event.target.value as OpsDocAccessLevel);
        }}
        value={level}
      >
        <option value="view">Can view</option>
        <option value="edit">Can edit</option>
        <option value="manage">Can manage</option>
      </select>
    </div>
  );
}

type ShareFormProps = {
  employees: OpsDocEmployeePrincipal[];
  error: Error | null;
  groups: OpsDocGroup[];
  isSaving: boolean;
  onSave: (input: {
    accessMode: OpsDocAccessMode;
    everyoneAccessLevel: "view" | "edit";
    entries: Entry[];
  }) => void;
  page: OpsDocPage;
};

function useShareEntries(page: OpsDocPage) {
  const [entries, setEntries] = useState<Entry[]>(
    page.accessEntries.map((entry) => ({
      subjectType: entry.subjectType,
      subjectId: entry.subjectId,
      accessLevel: entry.accessLevel,
    })),
  );
  const updateEntry = (
    subjectType: Entry["subjectType"],
    subjectId: string,
    next: Entry | null,
  ) => {
    setEntries((current) => [
      ...current.filter(
        (entry) => entry.subjectType !== subjectType || entry.subjectId !== subjectId,
      ),
      ...(next ? [next] : []),
    ]);
  };
  return { entries, updateEntry };
}

function AccessModeFields({
  everyoneLevel,
  mode,
  onEveryoneLevelChange,
  onModeChange,
  page,
}: {
  everyoneLevel: "view" | "edit";
  mode: OpsDocAccessMode;
  onEveryoneLevelChange: (level: "view" | "edit") => void;
  onModeChange: (mode: OpsDocAccessMode) => void;
  page: OpsDocPage;
}) {
  return (
    <>
      <label className="grid gap-1.5 text-sm font-semibold" htmlFor="docs-access-mode">
        Who can open this page
        <select
          className="min-h-10 rounded-sm border bg-card px-3 font-normal"
          id="docs-access-mode"
          onChange={(event) => {
            onModeChange(event.target.value as OpsDocAccessMode);
          }}
          value={mode}
        >
          {page.parentPageId === null ? null : <option value="inherit">Same as parent</option>}
          <option value="everyone">Everyone in this space</option>
          <option value="restricted">Selected people and groups</option>
          <option value="private">Only the owner</option>
        </select>
      </label>
      {mode === "everyone" ? (
        <label className="grid gap-1.5 text-sm font-semibold" htmlFor="docs-everyone-level">
          Everyone can
          <select
            className="min-h-10 rounded-sm border bg-card px-3 font-normal"
            id="docs-everyone-level"
            onChange={(event) => {
              onEveryoneLevelChange(event.target.value as "view" | "edit");
            }}
            value={everyoneLevel}
          >
            <option value="view">View</option>
            <option value="edit">Edit</option>
          </select>
        </label>
      ) : null}
    </>
  );
}

function SubjectSection({
  entries,
  empty,
  subjects,
  title,
  updateEntry,
}: {
  entries: Entry[];
  empty: string;
  subjects: { id: string; label: string; subjectType: Entry["subjectType"] }[];
  title: string;
  updateEntry: (type: Entry["subjectType"], id: string, entry: Entry | null) => void;
}) {
  const headingId = `share-${title.toLocaleLowerCase()}-title`;
  return (
    <section className="space-y-2" aria-labelledby={headingId}>
      <h3 className="text-sm font-bold" id={headingId}>
        {title}
      </h3>
      {subjects.length === 0 ? <p className="text-sm text-muted-foreground">{empty}</p> : null}
      {subjects.map((subject) => {
        const entry = entries.find(
          (candidate) =>
            candidate.subjectType === subject.subjectType && candidate.subjectId === subject.id,
        );
        return (
          <SubjectRow
            checked={entry !== undefined}
            key={`${subject.subjectType}:${subject.id}`}
            label={subject.label}
            level={entry?.accessLevel ?? "view"}
            onCheckedChange={(checked) => {
              updateEntry(
                subject.subjectType,
                subject.id,
                checked
                  ? {
                      subjectType: subject.subjectType,
                      subjectId: subject.id,
                      accessLevel: "view",
                    }
                  : null,
              );
            }}
            onLevelChange={(accessLevel) => {
              updateEntry(subject.subjectType, subject.id, {
                subjectType: subject.subjectType,
                subjectId: subject.id,
                accessLevel,
              });
            }}
          />
        );
      })}
    </section>
  );
}

export function RestrictedSubjects({
  employees,
  entries,
  groups,
  ownerEmployeeId,
  updateEntry,
}: Pick<ShareFormProps, "employees" | "groups"> & {
  entries: Entry[];
  ownerEmployeeId: string | null;
  updateEntry: ReturnType<typeof useShareEntries>["updateEntry"];
}) {
  return (
    <div className="space-y-4">
      <SubjectSection
        empty="No groups have been created."
        entries={entries}
        subjects={groups.map((group) => ({
          id: group.id,
          label: group.name,
          subjectType: "group",
        }))}
        title="Groups"
        updateEntry={updateEntry}
      />
      <SubjectSection
        empty="No other employees are available."
        entries={entries}
        subjects={employees
          .filter((employee) => employee.id !== ownerEmployeeId)
          .map((employee) => ({
            id: employee.id,
            label: employee.displayName,
            subjectType: "employee",
          }))}
        title="People"
        updateEntry={updateEntry}
      />
    </div>
  );
}

function ShareForm({ employees, error, groups, isSaving, onSave, page }: ShareFormProps) {
  const [mode, setMode] = useState(page.accessMode);
  const [everyoneLevel, setEveryoneLevel] = useState(page.everyoneAccessLevel);
  const { entries, updateEntry } = useShareEntries(page);
  return (
    <>
      <DialogBody>
        <AccessModeFields
          everyoneLevel={everyoneLevel}
          mode={mode}
          onEveryoneLevelChange={setEveryoneLevel}
          onModeChange={setMode}
          page={page}
        />
        {mode === "restricted" ? (
          <RestrictedSubjects
            employees={employees}
            entries={entries}
            groups={groups}
            ownerEmployeeId={page.ownerEmployeeId}
            updateEntry={updateEntry}
          />
        ) : null}
        {mode === "private" ? (
          <p className="flex items-center gap-2 rounded-sm border border-dashed p-3 text-sm text-muted-foreground">
            <LockKeyhole className="size-4" /> Only {page.ownerDisplayName} can open this page.
          </p>
        ) : null}
        {error ? <FieldError>{error.message}</FieldError> : null}
      </DialogBody>
      <DialogFooter>
        <Button
          disabled={isSaving}
          onClick={() => {
            onSave({
              accessMode: mode,
              everyoneAccessLevel: everyoneLevel,
              entries: mode === "restricted" ? entries : [],
            });
          }}
          type="button"
        >
          {isSaving ? "Saving…" : "Save access"}
        </Button>
      </DialogFooter>
    </>
  );
}

export function DocsShareDialog(props: ShareFormProps) {
  return (
    <Dialog>
      <DialogTrigger render={<Button variant="outline" />}>
        <Share2 /> Share
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Share {props.page.title}</DialogTitle>
          <DialogDescription>
            Space access and this page’s sharing are both required. Sharing a page does not grant
            access to its space.
          </DialogDescription>
        </DialogHeader>
        <ShareForm {...props} error={props.error} />
      </DialogContent>
    </Dialog>
  );
}
